import React, {
  useEffect, useId, useRef, useState,
} from 'react';
import { mtstatTrack } from '$/utils/mtstat';
import useTrackListExposure from '$/hooks/useTrackListExposure';
import NextLink from 'next/link';
import classNames from 'classnames';
import IconFont from '$/components/IconFont';
import NextImage from '$/components/NextImage';
import { useLanguageStore } from '$/store/languageStore';
import type { MyIpListState } from '$/hooks/myIp/useMyIpList';
import type { MyIpItem } from '$/domains/myIp/types';
import { useMyIpCreationCopy } from '$/pages/RoboneoMyIps/useMyIpCreationCopy';
import HomeIpCreationEntry from '@/pages/--roboneo/home/components/HomeIpCreationEntry';
import { useMyIpSession } from '@/hooks/myIp/useMyIpSession';
import { useMyIpDetailLinks } from '@/hooks/myIp/useMyIpBackNavigation';
import { handleImageUrl } from '$/utils/image';
import { navigate } from '@/common/agents';
import type { Config } from '@/common/config';
import createCardAvatar from './assets/create-card-avatar.png';
import style from './index.module.scss';

/** Next 静态图是 `{ src }`，Jest file mock 是字符串。 */
function staticAssetSrc(asset: string | { src: string; }) {
  return typeof asset === 'string' ? asset : asset.src;
}

const IP_CARD_WIDTH = 140;
const CREATE_CARD_WIDTH = 250;
const CARD_GAP = 12;
const CONTAINER_LEADING_PADDING = 40;

type MyIpsProps = {
  width?: number;
  ipState: MyIpListState & {
    loadedPages: number;
    refresh: () => void;
    loadMore: () => void;
  };
  onSlotCountChange?: (count: number) => void;
};

/** 服务端封面按卡片尺寸生成 mode 3 缩略图；加载失败时显示占位，地址更新后允许重新加载。 */
function IpCard({ item, href, position }: { item: MyIpItem; href: string; position: number; }) {
  const [failedUrl, setFailedUrl] = useState<string>();
  /** 通过统一路由导航进入 IP 详情，保留链接中的返回来源参数。 */
  const handleClick = () => {
    mtstatTrack('roboneo_home_ip_card_click', { ip_id: item.ipId, ip_name: item.name, position });
    // 详情链接由运行时拼出（含 query），需断言为应用 Pathname 联合类型。
    navigate({ mode: 'router', pathname: href as Config['Pathname'] });
  };

  return (
    <button type="button" className={style.ipCard} data-exposure-index={position} onClick={handleClick} title={item.name}>
      {item.coverUrl && item.coverUrl !== failedUrl ? (
        <NextImage
          className={style.cover}
          src={handleImageUrl(item.coverUrl, { mode: 3, width: IP_CARD_WIDTH, height: 180 })}
          alt=""
          crossOrigin="anonymous"
          useOriginalImage
          preserveSourceFormat
          draggable={false}
          onError={() => setFailedUrl(item.coverUrl)}
        />
      ) : (
        <span className={style.coverPlaceholder} aria-hidden="true">
          <IconFont name="portraitPhotoBold" fontSize={28} />
        </span>
      )}
      {item.coverUrl && item.coverUrl !== failedUrl && <span className={style.coverGradient} aria-hidden="true" />}
      <span className={style.cardName}>{item.name}</span>
    </button>
  );
}

/** 首页复用真实自有 IP 资格与分页；无自有 IP 只保留创建入口，不重定向首页。 */
export default function MyIps({ width, ipState: state, onSlotCountChange }: MyIpsProps) {
  const { home: copy } = useMyIpCreationCopy();
  const session = useMyIpSession();
  // router 模式始终接收 Next 内部路由，部署环境的公开路径由导航代理去除项目前缀。
  const getDetailHref = useMyIpDetailLinks(session.scopeKey, '/--roboneo/myips');
  const language = useLanguageStore((store) => store.languageStore);
  const listRef = useRef<HTMLDivElement>(null);
  const sectionId = useId();
  useTrackListExposure(listRef, 'roboneo_home_ip_card_show', state.list.map((item, position) => ({
    key: `${session.scopeKey}:${item.ipId}`, params: { ip_id: item.ipId, ip_name: item.name, position },
  })));
  const [edges, setEdges] = useState({ previous: false, next: false });
  const loading = state.status === 'idle' || state.status === 'loading';
  const retryText = language.smartcatTranslationText((text) => text.roboneo_web_retry);
  const loadMoreText = state.moreError != null ? retryText : language.smartcatTranslationText((text) => text.asset_load_more);
  const viewAllText = language.smartcatTranslationText((text) => text.view_all);
  const showListViewport = state.list.length > 0 || loading || state.status === 'error';
  const slotCount = showListViewport ? Math.max(state.list.length, 1) : 0;
  const fallbackWidth = slotCount > 0
    ? CONTAINER_LEADING_PADDING + CREATE_CARD_WIDTH + slotCount * (IP_CARD_WIDTH + CARD_GAP)
    : CONTAINER_LEADING_PADDING + CREATE_CARD_WIDTH;
  /** 将真实 IP 卡位数交给首页容器统一分配宽度。 */
  useEffect(() => {
    onSlotCountChange?.(slotCount);
  }, [onSlotCountChange, slotCount]);
  /** 换账号或业务空间时复位滚动，避免新账号从上一账号列表中间开始展示。 */
  useEffect(() => {
    if (listRef.current) listRef.current.scrollLeft = 0;
  }, [session.scopeKey]);

  /** 仅有可滚动内容的一侧显示遮罩和箭头，覆盖异步列表、分页与容器尺寸变化。 */
  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    /** 用像素容差消除缩放与平滑滚动产生的小数误差。 */
    const updateEdges = () => {
      const previous = list.scrollLeft > 1;
      const next = list.scrollWidth - list.clientWidth - list.scrollLeft > 1;
      setEdges((current) => (current.previous === previous && current.next === next ? current : { previous, next }));
    };
    updateEdges();
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateEdges);
    observer?.observe(list);
    list.addEventListener('scroll', updateEdges, { passive: true });
    window.addEventListener('resize', updateEdges);
    return () => {
      observer?.disconnect();
      list.removeEventListener('scroll', updateEdges);
      window.removeEventListener('resize', updateEdges);
    };
  }, [session.scopeKey, state.status, state.list.length, state.hasMore]);

  /** 沿用项目快捷入口的可见区滚动方式，保留一张卡宽度帮助用户衔接前后内容。 */
  const scroll = (direction: -1 | 1) => {
    const list = listRef.current;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    list?.scrollBy({ left: Math.max(list.clientWidth - 120, 132) * direction, behavior: reducedMotion ? 'auto' : 'smooth' });
  };

  return (
    <section
      className={style.container}
      style={{ '--my-ip-content-width': `${width ?? fallbackWidth}px` } as React.CSSProperties}
      aria-labelledby={`${sectionId}-title`}
    >
      <div className={style.header}>
        <h2 id={`${sectionId}-title`} className={style.title}>{copy.title}</h2>
        {state.list.length > 0 && (
          <NextLink onClick={() => mtstatTrack('roboneo_view_all_iplist_click', {})} className={style.viewAll} href={session.paths.list} aria-label={viewAllText}>
            <span>{viewAllText}</span>
            <IconFont name="chevronRightBlack" fontSize={14} />
          </NextLink>
        )}
      </div>
      <div className={style.cardViewport}>
        <HomeIpCreationEntry className={style.createCard}>
          <span className={style.createGlowPrimary} aria-hidden="true" />
          <span className={style.createGlowSecondary} aria-hidden="true" />
          <span className={style.createVisual} aria-hidden="true">
            <span className={style.createAvatar}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={staticAssetSrc(createCardAvatar)}
                alt=""
                className={style.createAvatarImage}
                draggable={false}
              />
            </span>
            <span className={style.createPlus}>
              <IconFont name="plusBlack" fontSize={16} />
            </span>
          </span>
          <span className={style.createLabel}>{copy.createCard}</span>
        </HomeIpCreationEntry>
        {showListViewport && (
          <div className={style.scrollViewport}>
            <div ref={listRef} id={`${sectionId}-list`} className={style.cardList} aria-busy={loading || state.loadingMore}>
              {state.list.map((item, position) => <IpCard key={item.ipId} item={item} position={position} href={getDetailHref(item.ipId)} />)}
              {loading && <p className={style.status} role="status" />}
              {state.status === 'error' && (
                <div className={style.status} role="alert">
                  <span>{language.smartcatTranslationText((text) => text.roboneo_web_loading_failed)}</span>
                  <button type="button" className={style.retry} onClick={state.refresh}>{retryText}</button>
                </div>
              )}
              {/* 角色 IP 无数据时保留创建入口和容器留白，不展示通用“暂无数据”文案。 */}
              {state.status === 'allowed' && state.hasMore && (
                <div className={style.status}>
                  {state.moreError != null && <span role="alert">{language.smartcatTranslationText((text) => text.load_more_fail)}</span>}
                  <button type="button" className={style.retry} disabled={state.loadingMore} onClick={state.loadMore}>
                    {state.loadingMore ? '' : loadMoreText}
                  </button>
                </div>
              )}
            </div>
            {edges.previous && (
              <>
                <span className={classNames(style.edgeMask, style.leftMask)} aria-hidden="true" />
                <button type="button" className={classNames(style.scrollButton, style.previous)} aria-label={copy.previous} aria-controls={`${sectionId}-list`} onClick={() => scroll(-1)}>
                  <IconFont name="arrowLeftBlack" fontSize={18} />
                </button>
              </>
            )}
            {edges.next && (
              <>
                <span className={classNames(style.edgeMask, style.rightMask)} aria-hidden="true" />
                <button type="button" className={classNames(style.scrollButton, style.next)} aria-label={copy.next} aria-controls={`${sectionId}-list`} onClick={() => scroll(1)}>
                  <IconFont name="arrowLeftBlack" fontSize={18} />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
